import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { COOKIE_CATEGORIES } from '../../constants/cookieConsent';
import {
  getDefaultConsentPreferences,
  getStoredCookieConsent,
  saveCookieConsent
} from '../../utils/cookieConsent';

const HIDDEN_PATH_PREFIXES = ['/admin', '/portal', '/app', '/superadmin'];

const CookieConsentManager = ({ onConsentResolved }) => {
  const { t } = useTranslation('legal');
  const { user, loading } = useAuth();
  const location = useLocation();

  const [showBanner, setShowBanner] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [preferences, setPreferences] = useState(getDefaultConsentPreferences);

  const isDashboardRoute = useMemo(
    () => HIDDEN_PATH_PREFIXES.some((prefix) => location.pathname.startsWith(prefix)),
    [location.pathname]
  );

  useEffect(() => {
    const storedConsent = getStoredCookieConsent();
    const storedPreferences = storedConsent?.preferences || getDefaultConsentPreferences();

    setPreferences(storedPreferences);

    if (typeof onConsentResolved === 'function') {
      onConsentResolved(storedPreferences);
    }

    if (loading || user || isDashboardRoute || storedConsent) {
      setShowBanner(false);
      setShowPreferencesModal(false);
      return;
    }

    setShowBanner(true);
  }, [loading, user, isDashboardRoute, onConsentResolved]);

  const handleAcceptAll = () => {
    const acceptedPreferences = {
      [COOKIE_CATEGORIES.ESSENTIAL]: true,
      [COOKIE_CATEGORIES.ANALYTICS]: true,
      [COOKIE_CATEGORIES.MARKETING]: true
    };
    saveCookieConsent({ preferences: acceptedPreferences, source: 'accept_all' });
    setPreferences(acceptedPreferences);
    setShowBanner(false);
    setShowPreferencesModal(false);
    if (typeof onConsentResolved === 'function') onConsentResolved(acceptedPreferences);
  };

  const handleRejectNonEssential = () => {
    const rejectedPreferences = getDefaultConsentPreferences();
    saveCookieConsent({ preferences: rejectedPreferences, source: 'reject_non_essential' });
    setPreferences(rejectedPreferences);
    setShowBanner(false);
    setShowPreferencesModal(false);
    if (typeof onConsentResolved === 'function') onConsentResolved(rejectedPreferences);
  };

  const handleSavePreferences = () => {
    const customPreferences = {
      [COOKIE_CATEGORIES.ESSENTIAL]: true,
      [COOKIE_CATEGORIES.ANALYTICS]: Boolean(preferences[COOKIE_CATEGORIES.ANALYTICS]),
      [COOKIE_CATEGORIES.MARKETING]: Boolean(preferences[COOKIE_CATEGORIES.MARKETING])
    };
    saveCookieConsent({ preferences: customPreferences, source: 'customize' });
    setPreferences(customPreferences);
    setShowBanner(false);
    setShowPreferencesModal(false);
    if (typeof onConsentResolved === 'function') onConsentResolved(customPreferences);
  };

  const togglePreference = (category) => {
    setPreferences((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  if (!showBanner) return null;

  return (
    <>
      <div className="fixed inset-x-0 bottom-4 z-[1200] px-4">
        <div className="mx-auto w-full max-w-3xl rounded-2xl border border-[#2f3640] bg-[#12161c]/95 p-4 shadow-2xl backdrop-blur-md sm:p-5">
          <p className="text-sm font-semibold text-[#f2f4f8] sm:text-base">{t('legal.cookie_banner_title')}</p>
          <p className="mt-2 text-xs leading-relaxed text-[#a9b1bc] sm:text-sm">
            {t('legal.cookie_banner_body')}
          </p>
          <div className="mt-2">
            <Link
              to="/cookie-policy"
              className="text-xs font-medium sm:text-sm"
              style={{ color: '#0097b2' }}
            >
              {t('legal.cookie_learn_more')}
            </Link>
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="rounded-xl border border-[#2f3640] px-4 py-2 text-sm font-medium text-[#d9dfe8] transition hover:bg-[#1b212a]"
              onClick={handleRejectNonEssential}
            >
              {t('legal.cookie_reject')}
            </button>
            <button
              type="button"
              className="rounded-xl border border-[#2f3640] px-4 py-2 text-sm font-medium text-[#d9dfe8] transition hover:bg-[#1b212a]"
              onClick={() => setShowPreferencesModal(true)}
            >
              {t('legal.cookie_customize')}
            </button>
            <button
              type="button"
              className="rounded-xl bg-[#7d6633] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#6c592d]"
              onClick={handleAcceptAll}
            >
              {t('legal.cookie_accept_all')}
            </button>
          </div>
        </div>
      </div>

      {showPreferencesModal && (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-xl rounded-2xl border border-[#2f3640] bg-[#10151c] p-5 shadow-2xl sm:p-6">
            <h2 className="text-lg font-semibold text-[#f2f4f8]">{t('legal.cookie_modal_title')}</h2>
            <p className="mt-2 text-sm text-[#a9b1bc]">
              {t('legal.cookie_modal_body')}
            </p>

            <div className="mt-5 space-y-3">
              <div className="rounded-xl border border-[#2f3640] bg-[#161d26] p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[#f2f4f8]">{t('legal.cookie_essential_title')}</p>
                    <p className="text-xs text-[#a9b1bc]">{t('legal.cookie_essential_desc')}</p>
                  </div>
                  <span className="rounded-full bg-[#2a313d] px-3 py-1 text-xs font-medium text-[#d9dfe8]">
                    {t('legal.cookie_essential_badge')}
                  </span>
                </div>
              </div>

              <div className="rounded-xl border border-[#2f3640] bg-[#161d26] p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-[#f2f4f8]">{t('legal.cookie_analytics_title')}</p>
                    <p className="text-xs text-[#a9b1bc]">{t('legal.cookie_analytics_desc')}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => togglePreference(COOKIE_CATEGORIES.ANALYTICS)}
                    className={`h-7 w-12 rounded-full p-1 transition ${
                      preferences[COOKIE_CATEGORIES.ANALYTICS] ? 'bg-[#7d6633]' : 'bg-[#313846]'
                    }`}
                    aria-pressed={preferences[COOKIE_CATEGORIES.ANALYTICS]}
                  >
                    <span
                      className={`block h-5 w-5 rounded-full bg-white transition ${
                        preferences[COOKIE_CATEGORIES.ANALYTICS] ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="rounded-xl border border-[#2f3640] bg-[#161d26] p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-[#f2f4f8]">{t('legal.cookie_marketing_title')}</p>
                    <p className="text-xs text-[#a9b1bc]">{t('legal.cookie_marketing_desc')}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => togglePreference(COOKIE_CATEGORIES.MARKETING)}
                    className={`h-7 w-12 rounded-full p-1 transition ${
                      preferences[COOKIE_CATEGORIES.MARKETING] ? 'bg-[#7d6633]' : 'bg-[#313846]'
                    }`}
                    aria-pressed={preferences[COOKIE_CATEGORIES.MARKETING]}
                  >
                    <span
                      className={`block h-5 w-5 rounded-full bg-white transition ${
                        preferences[COOKIE_CATEGORIES.MARKETING] ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                className="rounded-xl border border-[#2f3640] px-4 py-2 text-sm font-medium text-[#d9dfe8] transition hover:bg-[#1b212a]"
                onClick={() => setShowPreferencesModal(false)}
              >
                {t('legal.cookie_cancel')}
              </button>
              <button
                type="button"
                className="rounded-xl bg-[#7d6633] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#6c592d]"
                onClick={handleSavePreferences}
              >
                {t('legal.cookie_save_preferences')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsentManager;
